import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Layout components
import ServiceLayout from '../../../components/layout/ServiceLayout';
import ServicePageSection from '../../../components/layout/ServicePageSection';
import Container from '../../../components/layout/Container';

// UI components
import { FAQList } from '../../../components/ui/FAQ';
import CompactFeatureCard from '../../../components/ui/CompactFeatureCard';
import ProcessStep from '../../../components/ui/ProcessStep';
import PricingCard from '../../../components/ui/PricingCard';
import Card from '../../../components/ui/Card';

// Section components
import Hero from '../../../components/sections/Hero';
import CallToAction from '../../../components/sections/CallToAction';

// Data
import { softwareDevelopmentData } from '../../../data/servicePagesData';
import { caseStudies } from '../../../data/mockData';

/**
 * Custom Software Development Service Page
 */
const SoftwareDevelopmentPage: React.FC = () => {
  const relatedCaseStudies = caseStudies.filter((study) =>
    softwareDevelopmentData.relatedCaseStudies.includes(study.id),
  );

  return (
    <ServiceLayout>
      <Hero
        title={softwareDevelopmentData.title}
        subtitle={softwareDevelopmentData.subtitle}
        ctaText="Discuss Your Project"
        ctaLink="/contact"
        backgroundType="gradient"
      />

      {/* Overview */}
      <ServicePageSection title="Overview" padding="md">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-lg text-neutral-600 leading-relaxed">
            {softwareDevelopmentData.overview}
          </p>
        </div>
      </ServicePageSection>

      {/* Key Benefits */}
      <ServicePageSection title="Key Benefits" bgColor="gray" padding="md">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {softwareDevelopmentData.keyBenefits.map((benefit, index) => (
            <CompactFeatureCard
              key={benefit.title}
              title={benefit.title}
              description={benefit.description}
              delay={index * 0.1}
              icon={
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4"
                  />
                </svg>
              }
            />
          ))}
        </div>
      </ServicePageSection>

      {/* Features/Process */}
      <ServicePageSection title="Our Development Process" padding="md">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {softwareDevelopmentData.features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="h-full p-6 hover:shadow-lg transition-shadow duration-200">
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-neutral-600 mb-4">{feature.description}</p>

                <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                  <div>
                    <span className="font-medium text-neutral-800">Duration:</span>
                    <p className="text-neutral-600">{feature.duration}</p>
                  </div>
                  <div>
                    <span className="font-medium text-neutral-800">For:</span>
                    <p className="text-neutral-600">{feature.forWhom}</p>
                  </div>
                </div>

                <div className="mb-4">
                  <span className="font-medium text-neutral-800 block mb-2">What's Included:</span>
                  <ul className="text-sm text-neutral-600 space-y-1">
                    {feature.features.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-primary mr-2">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </ServicePageSection>

      {/* Implementation Process */}
      <ServicePageSection title="Implementation Process" bgColor="gray" padding="md">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {softwareDevelopmentData.implementationProcess.map((step, index) => (
              <ProcessStep
                key={step.step}
                number={step.step}
                title={step.title}
                description={step.description}
              />
            ))}
          </div>
        </div>
      </ServicePageSection>

      {/* Pricing Plans - disabled */
      false && (
      <ServicePageSection title="Pricing Plans" padding="md">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {softwareDevelopmentData.pricingTiers.map((tier, index) => (
              <PricingCard
                key={tier.name}
                name={tier.name}
                description={tier.description}
                price={tier.price === 'Custom' ? null : parseInt(tier.price.replace(/[^0-9]/g, ''))}
                billing={tier.perUnit}
                features={tier.features}
                mostPopular={tier.isPopular}
                ctaText={tier.ctaText}
                ctaLink="/contact"
              />
            ))}
          </div>
        </div>
      </ServicePageSection>
      )}

      {/* Related Case Studies */}
      <ServicePageSection title="Success Stories" padding="md">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {relatedCaseStudies.slice(0, 2).map((study, index) => (
            <motion.div
              key={study.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Link to={study.url} className="block h-full">
                <Card hover className="h-full p-6">
                  <div className="mb-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      {study.solution}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{study.title}</h3>
                  <p className="text-neutral-600 text-sm mb-4">{study.excerpt}</p>
                  <div className="space-y-2">
                    {study.results.slice(0, 2).map((result, idx) => (
                      <div key={idx} className="flex items-start text-sm">
                        <svg className="w-4 h-4 text-primary mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-neutral-600">{result}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-neutral-100">
                    <span className="inline-flex items-center text-primary font-medium text-sm">
                      Read full case study
                      <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link
            to="/case-studies"
            className="inline-flex items-center text-primary font-medium hover:text-primary-700 transition-colors"
          >
            View all case studies
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </ServicePageSection>

      {/* FAQ */}
      <ServicePageSection title="Frequently Asked Questions" bgColor="gray" padding="md">
        <div className="max-w-3xl mx-auto">
          <FAQList faqs={softwareDevelopmentData.faqs} />
        </div>
      </ServicePageSection>

      {/* Call to Action */}
      <CallToAction
        title="Ready to Build Custom Software?"
        description="Let's discuss your project requirements and create scalable software solutions that drive your business forward."
        primaryBtnText="Start Your Development Project"
        primaryBtnUrl="/contact"
      />
    </ServiceLayout>
  );
};

export default SoftwareDevelopmentPage;
